import { motion, AnimatePresence } from "framer-motion";
import { X, Link2, Globe, Coins, QrCode } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const trackClick = (bizId, kind) =>
  axios.post(`${BACKEND_URL}/api/pog-ads/${encodeURIComponent(bizId)}/click`, { kind }).catch(() => {});

const ActionBtn = ({ testId, onClick, disabled, label, children, tone }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`flex flex-col items-center gap-1.5 flex-1 min-w-0 group ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
  >
    <span className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border transition-all ${disabled ? "bg-slate-800/60 border-slate-700/60" : `${tone} group-hover:-translate-y-1 group-active:scale-95`}`}>
      {children}
    </span>
    <span className="text-[10px] font-heading font-extrabold uppercase tracking-wide text-slate-300 leading-tight text-center">{label}</span>
  </button>
);

// Expanded POG AD — pops above the strip when a driver taps a merchant's POG AD card.
export const PogAdPanel = ({ pogAd, onClose, onRoute, onRedeem, openLink }) => {
  if (!pogAd) return null;
  const b = pogAd.biz;
  const links = b.links || {};
  const isAndroid = /android/i.test(navigator.userAgent);
  const appLink = isAndroid ? (links.android_app || links.ios_app) : (links.ios_app || links.android_app);
  const pogLink = links.pog_link || links.order_url;
  const deal = (pogAd.deals || [])[0];
  return (
    <AnimatePresence>
      <motion.div
        className="absolute inset-x-0 bottom-0 top-0 z-[1900] flex items-end justify-center bg-black/40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          data-testid="pog-ad-panel"
          initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
          transition={{ type: "spring", damping: 26, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-t-3xl w-full max-w-md relative overflow-hidden mb-[calc(9.5rem+env(safe-area-inset-bottom))] mx-3 rounded-3xl"
        >
          <div className="relative h-36">
            <img src={`${BACKEND_URL}${b.ad_image_url}`} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/10" />
            <span className="absolute top-2.5 left-3 z-10 text-[9px] font-extrabold uppercase tracking-[0.2em] text-white bg-fuchsia-500/80 rounded-full px-2.5 py-1 shadow-lg">POG AD</span>
            {pogAd.bucks > 0 && (
              <span data-testid="pog-ad-bucks" className="absolute top-2.5 right-12 z-10 flex items-center gap-1 text-[10px] font-extrabold text-amber-300 bg-slate-900/80 rounded-full px-2.5 py-1">
                <Coins className="w-3 h-3" /> +{pogAd.bucks} POG BUCK
              </span>
            )}
            <button data-testid="pog-ad-close" onClick={onClose} className="absolute top-1.5 right-1.5 z-10 w-9 h-9 rounded-xl bg-slate-900/60 hover:bg-slate-900/90 flex items-center justify-center">
              <X className="w-5 h-5 text-white" />
            </button>
            <div className="absolute bottom-0 inset-x-0 z-10 px-4 pb-2 flex items-end gap-2.5">
              {b.logo_url && (
                <span className="w-11 h-11 rounded-xl bg-white shadow-lg overflow-hidden shrink-0 flex items-center justify-center">
                  <img src={`${BACKEND_URL}${b.logo_url}`} alt="" className="w-9 h-9 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </span>
              )}
              <div className="min-w-0">
                <p data-testid="pog-ad-panel-headline" className="font-heading font-black text-lg leading-tight text-white line-clamp-2" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.8)" }}>{b.ad_headline}</p>
                <p className="text-xs font-bold text-sky-300" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{b.name} · {b.distance_miles} mi</p>
              </div>
            </div>
          </div>

          <div className="p-4 pt-3">
            <div className="flex gap-2">
              <ActionBtn testId="pog-ad-route-btn" onClick={() => { trackClick(b.id, "route"); onRoute(b); }} label="POG'N GO" tone="bg-emerald-500/15 border-emerald-400/40">
                🎯
              </ActionBtn>
              <ActionBtn testId="pog-ad-link-btn" disabled={!pogLink} onClick={() => { if (pogLink) { trackClick(b.id, "pog_link"); openLink(b.id, "pog_link", pogLink); } }} label="POG Link" tone="bg-fuchsia-500/15 border-fuchsia-400/40">
                <Link2 className="w-6 h-6 text-fuchsia-300" />
              </ActionBtn>
              <ActionBtn testId="pog-ad-app-btn" disabled={!appLink} onClick={() => { if (appLink) { trackClick(b.id, "app"); openLink(b.id, "app", appLink); } }} label="Get the App" tone="bg-sky-500/15 border-sky-400/40">
                <span className="font-heading font-black text-2xl text-sky-300">A</span>
              </ActionBtn>
              <ActionBtn testId="pog-ad-web-btn" disabled={!links.website} onClick={() => { if (links.website) { trackClick(b.id, "website"); openLink(b.id, "website", links.website); } }} label="Website" tone="bg-indigo-500/15 border-indigo-400/40">
                <Globe className="w-6 h-6 text-indigo-300" />
              </ActionBtn>
              <ActionBtn testId="pog-ad-redeem-btn" disabled={!deal} onClick={() => { if (deal) { trackClick(b.id, "redeem"); onRedeem(deal, b); } }} label="Redeem" tone="bg-amber-400/20 border-amber-400/60">
                <QrCode className="w-6 h-6 text-amber-300" />
              </ActionBtn>
            </div>

            {deal && (
              <p data-testid="pog-ad-deal-teaser" className="mt-3 text-center text-xs font-heading font-bold text-amber-300 leading-snug line-clamp-1">
                🎟️ {deal.discount ? `${deal.discount} — ` : ""}{deal.title}
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
